import type { ExperienceDSL } from '../dsl';
import type { SceneManifest } from '../manifest';

export const royalDslFixture: ExperienceDSL = {
  version: '1.0',
  template: 'royal',
  plan: 'pro',
  meta: {
    brideName: 'Priya',
    groomName: 'Arjun',
    weddingDate: '2025-02-14',
    tagline: 'Two souls, one eternal bond',
    referenceImages: {
      bride: 'https://placehold.co/800x1000/jpg',
      groom: 'https://placehold.co/800x1000/jpg',
      couple: 'https://placehold.co/1200x800/jpg',
    },
  },
  rituals: [
    {
      ritualId: 'engagement',
      order: 0,
      date: '2025-02-10',
      time: '6:00 PM',
      venue: { name: 'The Grand Ballroom', address: '123 Palace Road, Mumbai' },
      emotionalArc: { primary: 'romantic', intensity: 'moderate' },
    },
    {
      ritualId: 'haldi',
      order: 1,
      date: '2025-02-13',
      time: '10:00 AM',
      venue: { name: 'Family Home', address: '45 Garden Lane, Pune' },
      emotionalArc: { primary: 'festive', intensity: 'dramatic' },
      customNote: 'Yellow attire requested',
    },
    {
      ritualId: 'antarpat',
      order: 2,
      date: '2025-02-14',
      time: '9:00 AM',
      venue: { name: 'Royal Palace Hall', address: 'Grand Avenue, Mumbai' },
      emotionalArc: { primary: 'sacred', intensity: 'subtle' },
    },
    {
      ritualId: 'varmala',
      order: 3,
      date: '2025-02-14',
      time: '11:00 AM',
      venue: { name: 'Royal Palace Hall', address: 'Grand Avenue, Mumbai' },
      emotionalArc: { primary: 'celebratory', intensity: 'dramatic' },
    },
    {
      ritualId: 'saptapadi',
      order: 4,
      date: '2025-02-14',
      time: '12:00 PM',
      venue: { name: 'Royal Palace Hall', address: 'Grand Avenue, Mumbai' },
      emotionalArc: { primary: 'sacred', intensity: 'moderate' },
    },
  ],
};

const placeholderImage = {
  publicUrl: '/assets/wow/gemini-wedding-hero.png',
  mimeType: 'image/png',
  sizeBytes: 2097152,
  dimensions: { width: 1024, height: 1024 },
  provider: 'gemini_image',
};

export const royalManifestFixture: SceneManifest = {
  version: '1.0',
  invitationId: 'fixture-invitation-id',
  generatedAt: '2025-01-01T00:00:00.000Z',
  template: 'royal',
  opening: {
    enabled: true,
    title: 'With the blessings of Ganesh',
    subtitle: 'Tap to open the kankotri',
    skipStorageKey: 'invitecloud:opened:fixture-royal-heritage',
  },
  typography: { nameTreatment: 'calligraphy_reveal', foil: 'gold', monogram: 'PA' },
  hero: {
    brideName: 'Priya',
    groomName: 'Arjun',
    weddingDate: '2025-02-14',
    tagline: 'Two souls, one eternal bond',
    background: {
      renderStrategy: 'image_primary',
      primaryImage: placeholderImage,
      particleEffect: 'petals',
    },
    decor: {
      frame: { url: '/assets/royal-ganesh-interactive/extracted/logo/logo.png', alt: 'Wedding floral ornament' },
      corner: { url: '/assets/royal-ganesh-interactive/extracted/logo/logo.png', alt: 'Wedding floral ornament' },
      intensity: 'ornate',
    },
  },
  sections: [
    {
      sectionId: 'engagement-0',
      momentId: 'engagement',
      ritualId: 'engagement',
      order: 0,
      content: {
        title: 'Ring Ceremony',
        date: '2025-02-10',
        time: '6:00 PM',
        venue: { name: 'The Grand Ballroom', address: '123 Palace Road, Mumbai' },
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: placeholderImage,
        particleEffect: 'sparkles',
      },
      animation: { entry: 'fade_up', parallax: { enabled: true, intensity: 0.3 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: true, reduceParticles: true },
      decor: { intensity: 'subtle' },
    },
    {
      sectionId: 'haldi-1',
      momentId: 'haldi',
      ritualId: 'haldi',
      order: 1,
      content: {
        title: 'Haldi Ceremony',
        date: '2025-02-13',
        time: '10:00 AM',
        venue: { name: 'Family Home', address: '45 Garden Lane, Pune' },
        customNote: 'Yellow attire requested',
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: placeholderImage,
        particleEffect: 'yellow_dust',
      },
      animation: { entry: 'color_wash', parallax: { enabled: true, intensity: 0.4 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: true, reduceParticles: true },
      decor: { intensity: 'subtle' },
      interactive: { kind: 'light_diya' },
    },
    {
      sectionId: 'antarpat-2',
      momentId: 'antarpat',
      ritualId: 'antarpat',
      order: 2,
      content: {
        title: 'Antarpat',
        date: '2025-02-14',
        time: '9:00 AM',
        venue: { name: 'Royal Palace Hall', address: 'Grand Avenue, Mumbai' },
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: placeholderImage,
        particleEffect: 'petals',
      },
      animation: { entry: 'curtain_reveal', parallax: { enabled: true, intensity: 0.2 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: true, reduceParticles: true },
      decor: { intensity: 'subtle' },
      interactive: { kind: 'lift_veil' },
    },
    {
      sectionId: 'varmala-3',
      momentId: 'varmala',
      ritualId: 'varmala',
      order: 3,
      content: {
        title: 'Varmala',
        date: '2025-02-14',
        time: '11:00 AM',
        venue: { name: 'Royal Palace Hall', address: 'Grand Avenue, Mumbai' },
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: placeholderImage,
        particleEffect: 'petals',
      },
      animation: { entry: 'zoom_in', parallax: { enabled: true, intensity: 0.5 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: true, reduceParticles: true },
      decor: { intensity: 'subtle' },
      interactive: { kind: 'petal_shower' },
    },
    {
      sectionId: 'saptapadi-4',
      momentId: 'saptapadi',
      ritualId: 'saptapadi',
      order: 4,
      content: {
        title: 'Saptapadi',
        subtitle: 'Seven sacred vows',
        date: '2025-02-14',
        time: '12:00 PM',
        venue: { name: 'Royal Palace Hall', address: 'Grand Avenue, Mumbai' },
      },
      assets: {
        renderStrategy: 'image_primary',
        primaryImage: placeholderImage,
        particleEffect: 'embers',
      },
      animation: { entry: 'fade_up', parallax: { enabled: true, intensity: 0.3 }, scrollTriggerThreshold: 0.3 },
      mobileBehavior: { disableVideo: true, reduceParticles: true },
      decor: { intensity: 'subtle' },
    },
  ],
  footer: {
    venue: { name: 'Royal Palace Hall', address: 'Grand Avenue, Mumbai' },
    rsvpLink: 'https://invitecloud.in/rsvp/fixture',
    calendar: {
      title: 'Priya & Arjun Wedding',
      startsAt: '2025-02-14T09:00:00+05:30',
      endsAt: '2025-02-14T18:00:00+05:30',
    },
  },
};
